import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Clock, AlertTriangle, Package, Search,
  ChevronDown, ChevronRight, ArrowUpDown, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const AGING_BUCKETS = [
  { key: '0_30', label: '0–30 days', color: 'bg-emerald-500', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50' },
  { key: '31_60', label: '31–60 days', color: 'bg-amber-400', textColor: 'text-amber-700', bgLight: 'bg-amber-50' },
  { key: '61_90', label: '61–90 days', color: 'bg-orange-500', textColor: 'text-orange-700', bgLight: 'bg-orange-50' },
  { key: 'over_90', label: '90+ days', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50' },
];

const ITEM_TYPES = ['All', 'Raw Material', 'Semi-Finished Part', 'Finished Good', 'Consumable', 'Fixed Asset', 'Job Work Item', 'Scrap'];

export default function StockAgingReportPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [threshold, setThreshold] = useState(90);
  const [itemTypeFilter, setItemTypeFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [sortField, setSortField] = useState('oldest_age_days');
  const [sortDir, setSortDir] = useState('desc');

  const loadReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { threshold_days: threshold };
      if (itemTypeFilter !== 'All') params.item_type = itemTypeFilter;
      const res = await inventoryAPI.getStockAging(params);
      setData(res.data);
    } catch {
      toast.error('Failed to load stock aging report');
    } finally {
      setLoading(false);
    }
  }, [threshold, itemTypeFilter]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const filteredItems = (data?.items || [])
    .filter(item => {
      if (!searchTerm) return true;
      const q = searchTerm.toLowerCase();
      return item.item_name.toLowerCase().includes(q) || item.unique_id.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      let va = a[sortField], vb = b[sortField];
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const exportCSV = () => {
    if (!filteredItems.length) return;
    const rows = [['Item Name', 'Unique ID', 'Type', 'UOM', 'Lots', 'Total Remaining', 'Oldest Age (Days)']];
    filteredItems.forEach(item => {
      rows.push([item.item_name, item.unique_id, item.item_type, item.uom, item.lot_count, item.total_remaining.toFixed(2), item.oldest_age_days]);
      item.lots.forEach(lot => {
        rows.push([`  Lot: ${lot.lot_id.slice(0, 8)}`, lot.godown_name, '', '', lot.received_date, lot.remaining_quantity.toFixed(2), lot.age_days]);
      });
    });
    const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Stock_Aging_Report_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalQty = data ? Object.values(data.buckets).reduce((s, b) => s + b.quantity, 0) : 0;

  return (
    <div className="space-y-5" data-testid="stock-aging-report-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <Clock className="w-5 h-5 text-orange-600" />
            Stock Aging Report
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Identify slow-moving inventory with lots beyond the configured age threshold.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} disabled={!filteredItems.length} data-testid="export-csv-btn">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Threshold (days)</Label>
              <Input
                type="number" min={1} max={999} className="w-24 h-8 text-sm"
                value={threshold} onChange={e => setThreshold(Math.max(1, parseInt(e.target.value) || 90))}
                data-testid="threshold-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Item Type</Label>
              <Select value={itemTypeFilter} onValueChange={setItemTypeFilter}>
                <SelectTrigger className="w-44 h-8 text-sm" data-testid="item-type-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 min-w-[180px]">
              <Label className="text-xs">Search</Label>
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Item name or ID..." className="pl-8 h-8 text-sm"
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                  data-testid="search-input"
                />
              </div>
            </div>
            <Button size="sm" className="h-8" onClick={loadReport} data-testid="refresh-btn">Refresh</Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground text-sm" data-testid="loading-indicator">Loading report...</div>
      ) : !data ? (
        <div className="text-center py-16 text-muted-foreground text-sm">No data available.</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {AGING_BUCKETS.map(bucket => {
              const b = data.buckets[bucket.key];
              const pct = data.total_lots > 0 ? ((b.lots / data.total_lots) * 100).toFixed(0) : 0;
              return (
                <Card key={bucket.key} className={`${bucket.bgLight} border-none`} data-testid={`bucket-card-${bucket.key}`}>
                  <CardContent className="pt-3 pb-3 px-4">
                    <p className={`text-xs font-medium ${bucket.textColor}`}>{bucket.label}</p>
                    <p className="text-2xl font-bold mt-1">{b.lots}</p>
                    <p className="text-xs text-muted-foreground">lots &middot; {b.quantity.toFixed(1)} qty &middot; {pct}%</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Distribution Bar */}
          <Card>
            <CardContent className="pt-3 pb-3 px-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-muted-foreground">Lot Distribution by Age</span>
                <span className="text-xs text-muted-foreground">{data.total_lots} lots &middot; {totalQty.toFixed(1)} total qty</span>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden bg-muted" data-testid="distribution-bar">
                {AGING_BUCKETS.map(bucket => {
                  const b = data.buckets[bucket.key];
                  const pct = data.total_lots > 0 ? (b.lots / data.total_lots) * 100 : 0;
                  if (pct === 0) return null;
                  return <div key={bucket.key} className={`${bucket.color}`} style={{ width: `${pct}%` }} title={`${bucket.label}: ${b.lots} lots`} />;
                })}
              </div>
              <div className="flex flex-wrap gap-3 mt-2">
                {AGING_BUCKETS.map(bucket => (
                  <span key={bucket.key} className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <span className={`w-2 h-2 rounded-full ${bucket.color}`} />
                    {bucket.label}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Flagged Alert */}
          {data.flagged_items_count > 0 && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="flagged-alert">
              <AlertTriangle className="w-4 h-4 flex-shrink-0" />
              <span><strong>{data.flagged_items_count}</strong> item{data.flagged_items_count !== 1 ? 's have' : ' has'} lots older than <strong>{data.threshold_days}</strong> days — consider reviewing for disposal, reuse, or write-off.</span>
            </div>
          )}

          {/* Items Table */}
          <Card>
            <CardHeader className="pb-2 pt-3 px-4">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5">
                <Package className="w-4 h-4" /> Items with Active Stock ({filteredItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-2">
              {filteredItems.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm">No items match the current filters.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="aging-items-table">
                    <thead>
                      <tr className="border-b border-border text-left text-xs text-muted-foreground">
                        <th className="p-2.5 pl-4 w-8" />
                        <th className="p-2.5 cursor-pointer select-none" onClick={() => handleSort('item_name')}>
                          <span className="flex items-center gap-1">Item <ArrowUpDown className="w-3 h-3" /></span>
                        </th>
                        <th className="p-2.5">Type</th>
                        <th className="p-2.5 text-right">Lots</th>
                        <th className="p-2.5 text-right cursor-pointer select-none" onClick={() => handleSort('total_remaining')}>
                          <span className="flex items-center justify-end gap-1">Remaining <ArrowUpDown className="w-3 h-3" /></span>
                        </th>
                        <th className="p-2.5 text-right cursor-pointer select-none" onClick={() => handleSort('oldest_age_days')}>
                          <span className="flex items-center justify-end gap-1">Oldest Lot <ArrowUpDown className="w-3 h-3" /></span>
                        </th>
                        <th className="p-2.5 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredItems.map(item => {
                        const isExpanded = expandedItems[item.item_id];
                        const isFlagged = item.oldest_age_days > data.threshold_days;
                        return (
                          <React.Fragment key={item.item_id}>
                            <tr
                              className={`border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/40 ${isFlagged ? 'bg-red-50/40' : ''}`}
                              onClick={() => toggleExpand(item.item_id)}
                              data-testid={`aging-item-row-${item.item_id}`}
                            >
                              <td className="p-2.5 pl-4">
                                {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                              </td>
                              <td className="p-2.5">
                                <Link to={`/inventory/items/${item.item_id}`} className="font-medium text-foreground hover:text-orange-600 hover:underline" onClick={e => e.stopPropagation()}>
                                  {item.item_name}
                                </Link>
                                {item.unique_id && <span className="block text-[10px] text-muted-foreground">{item.unique_id}</span>}
                              </td>
                              <td className="p-2.5">
                                <Badge variant="outline" className="text-[10px]">{item.item_type}</Badge>
                              </td>
                              <td className="p-2.5 text-right font-mono">{item.lot_count}</td>
                              <td className="p-2.5 text-right font-mono">{item.total_remaining.toFixed(2)} <span className="text-[10px] text-muted-foreground">{item.uom}</span></td>
                              <td className="p-2.5 text-right">
                                <span className={`font-mono font-semibold ${isFlagged ? 'text-red-600' : 'text-foreground'}`}>{item.oldest_age_days}d</span>
                              </td>
                              <td className="p-2.5 text-center">
                                {isFlagged
                                  ? <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">Slow</Badge>
                                  : <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">OK</Badge>
                                }
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr data-testid={`aging-item-lots-${item.item_id}`}>
                                <td colSpan={7} className="px-4 pb-3 pt-1">
                                  <div className="bg-muted/30 rounded-md border border-border/50 overflow-hidden">
                                    <table className="w-full text-xs">
                                      <thead>
                                        <tr className="text-muted-foreground border-b border-border/50">
                                          <th className="p-2 text-left">Lot ID</th>
                                          <th className="p-2 text-left">Godown</th>
                                          <th className="p-2 text-left">Received</th>
                                          <th className="p-2 text-right">Original</th>
                                          <th className="p-2 text-right">Remaining</th>
                                          <th className="p-2 text-right">Age</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {item.lots.map(lot => {
                                          const lotFlagged = lot.age_days > data.threshold_days;
                                          return (
                                            <tr key={lot.lot_id} className={`border-b border-border/30 last:border-0 ${lotFlagged ? 'bg-red-50/60' : ''}`}>
                                              <td className="p-2 font-mono text-muted-foreground">{lot.lot_id.slice(0, 8)}...</td>
                                              <td className="p-2">{lot.godown_name}{lot.godown_code ? ` (${lot.godown_code})` : ''}</td>
                                              <td className="p-2">{lot.received_date}</td>
                                              <td className="p-2 text-right font-mono">{lot.original_quantity.toFixed(2)}</td>
                                              <td className="p-2 text-right font-mono font-semibold">{lot.remaining_quantity.toFixed(2)}</td>
                                              <td className="p-2 text-right">
                                                <span className={`font-mono ${lotFlagged ? 'text-red-600 font-semibold' : ''}`}>{lot.age_days}d</span>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
